import React from 'react'
import ReactDOM from 'react-dom/client'

type WithChildren<T> = T & { children?: React.ReactNode }
type ReactComponent<P> = React.FC<P> | ((props: P) => React.ReactElement | null)

export function createReactCustomElement<Props = {}>(
  tagName: string,
  Component: ReactComponent<WithChildren<Props>>
) {
  class ReactCustomElement extends HTMLElement {
    connectedCallback() {
      if (this.shadowRoot) return

      const shadow = this.attachShadow({ mode: 'open' })
      const mountPoint = document.createElement('div')
      shadow.appendChild(mountPoint)

      // Collect all attributes with coercion
      const rawProps: Record<string, any> = {}
      for (const attr of this.getAttributeNames()) {
        rawProps[attr] = coerceType(this.getAttribute(attr))
      }

      // Merge id and children explicitly
      const props = {
        ...rawProps,
        id: this.id || undefined,
        children: this.textContent ? this.textContent : undefined,
      } as unknown as Partial<WithChildren<Props>>

      const root = ReactDOM.createRoot(mountPoint)
      root.render(<Component {...(props as WithChildren<Props>)} />)
    }
  }

  if (!customElements.get(tagName)) {
    customElements.define(tagName, ReactCustomElement)
  }
}

function coerceType(value: string | null): any {
  if (value === null) return null
  if (value === 'true') return true
  if (value === 'false') return false
  if (!isNaN(Number(value)) && value.trim() !== '') return Number(value)
  return value
}
